import { useState, useEffect } from "react";
import { MapPin, ZoomIn, X } from "lucide-react";
import { GALLERY, GALLERY_CATEGORIES } from "@/data/content";

const GalleryCard = ({ item, onZoom }) => {
  const [showAfter, setShowAfter] = useState(true);

  if (item.image) {
    return (
      <div data-testid="gallery-card" className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-xl transition-shadow duration-300">
        <button
          onClick={() => onZoom(item.image, item.name)}
          data-testid="gallery-zoom-btn"
          className="relative aspect-[4/3] overflow-hidden block w-full cursor-zoom-in"
          aria-label={`View ${item.name}`}
        >
          <img src={item.image} alt={item.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <span className="absolute top-3 left-3 text-xs font-bold uppercase tracking-wide bg-[#e02329] text-white px-3 py-1 rounded-sm">Real Project</span>
          <span className="absolute bottom-3 right-3 text-xs font-bold uppercase tracking-wide bg-[#1c4f9c] text-white px-3 py-1 rounded-sm">{item.category}</span>
          <span className="absolute inset-0 flex items-center justify-center bg-[#1c4f9c]/0 group-hover:bg-[#1c4f9c]/30 transition-colors duration-300">
            <ZoomIn size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </span>
        </button>
        <div className="p-6">
          <h3 className="text-lg font-bold text-[#1c4f9c]">{item.name}</h3>
          <p className="flex items-center gap-1.5 text-sm text-slate-500 mt-1"><MapPin size={14} /> {item.location}</p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.description}</p>
        </div>
      </div>
    );
  }

  const activeImg = showAfter ? item.after : item.before;
  return (
    <div data-testid="gallery-card" className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={activeImg} alt={item.name} loading="lazy" decoding="async" onClick={() => onZoom(activeImg, item.name)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 cursor-zoom-in" />
        <div className="absolute top-3 left-3 flex gap-1.5">
          <button
            onClick={() => setShowAfter(false)}
            data-testid="gallery-before-btn"
            className={`text-xs font-bold px-3 py-1 rounded-sm transition-colors ${!showAfter ? "bg-[#e02329] text-white" : "bg-white/90 text-slate-700"}`}
          >
            Before
          </button>
          <button
            onClick={() => setShowAfter(true)}
            data-testid="gallery-after-btn"
            className={`text-xs font-bold px-3 py-1 rounded-sm transition-colors ${showAfter ? "bg-[#1c4f9c] text-white" : "bg-white/90 text-slate-700"}`}
          >
            After
          </button>
        </div>
        <span className="absolute bottom-3 right-3 text-xs font-bold uppercase tracking-wide bg-[#1c4f9c] text-white px-3 py-1 rounded-sm">{item.category}</span>
      </div>
      <div className="p-6">
        <h3 className="text-lg font-bold text-[#1c4f9c]">{item.name}</h3>
        <p className="flex items-center gap-1.5 text-sm text-slate-500 mt-1"><MapPin size={14} /> {item.location}</p>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
};

export const Gallery = () => {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const filtered = active === "All" ? GALLERY : GALLERY.filter((g) => g.category === active);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-6">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e02329] mb-3">Recent Projects</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1c4f9c]">
            See the Elite difference, before & after
          </h2>
        </div>

        <p data-testid="trusted-by" className="text-sm font-semibold text-slate-500 mb-10">
          Trusted by Florida businesses — builders, property managers, offices & retail brands.
        </p>

        <div className="flex flex-wrap gap-2 mb-12">
          {GALLERY_CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`gallery-filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setActive(cat)}
              className={`text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors duration-200 ${
                active === cat ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600 hover:border-[#1c4f9c]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((item) => (
            <GalleryCard key={item.name} item={item} onZoom={(src, name) => setLightbox({ src, name })} />
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 sm:p-10 animate-fade-up"
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-[#e02329] text-white rounded-sm transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.name} className="w-full max-h-[80vh] object-contain rounded-sm" />
            <figcaption className="mt-4 text-center text-white font-semibold">{lightbox.name}</figcaption>
          </figure>
        </div>
      )}
    </section>
  );
};
